// SitePlugins — connect/disconnect URL-based integrations.
//
// Default view shows the integrations the customer picked in the
// onboarding wizard (brief.integrations). Below that, a small
// "Add another" footer of chips lets them connect more without going
// back through the wizard. OAuth-only services (Stripe, Mailchimp,
// etc.) get a "Coming soon" section so the customer can SEE they
// exist — silent-hide created "is this broken?" confusion.
//
// Saving writes to the brief (and job docs for site-api-served
// surfaces). Published sites are STATIC bundles, so a connection only
// shows up after the next build — the earlier "updates in ~30
// seconds" toast promised a live pickup that static serving never
// delivers (staging notes: "added Etsy … nothing happened"). Be
// truthful and hand the customer the rebuild lever instead.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type BusinessType, type Plugin } from "../../lib/api";
import { INTEGRATIONS, integrationsForType, type IntegrationDef } from "../../lib/catalog";

// Integrations that need OAuth / API keys — shown in a "Coming soon"
// row with a disabled affordance instead of being silently hidden.
const OAUTH_INTEGRATIONS = new Set([
  "stripe", "square", "mailchimp", "klaviyo", "twilio", "quickbooks",
  "jobber", "housecall-pro", "docusign", "simplepractice", "mindbody",
]);

const PLACEHOLDER: Record<string, string> = {
  opentable:    "https://www.opentable.com/r/your-restaurant",
  resy:         "https://resy.com/cities/...",
  toast:        "https://www.toasttab.com/...",
  doordash:     "https://www.doordash.com/store/...",
  ubereats:     "https://www.ubereats.com/store/...",
  grubhub:      "https://www.grubhub.com/restaurant/...",
  shopify:      "https://your-store.myshopify.com",
  etsy:         "https://www.etsy.com/shop/yourshop",
  amazon:       "https://www.amazon.com/stores/yourbrand",
  "tiktok-shop": "https://www.tiktok.com/@yourshop",
  calendly:     "https://calendly.com/yourname",
  acuity:       "https://yourname.as.me",
  "google-calendar":    "https://calendar.google.com/...",
  "square-appointments": "https://book.squareup.com/...",
  "google-business":    "https://g.page/your-business",
  yelp:         "https://www.yelp.com/biz/your-business",
  thumbtack:    "https://www.thumbtack.com/...",
  angi:         "https://www.angi.com/...",
  instagram:    "https://instagram.com/yourhandle",
  tiktok:       "https://tiktok.com/@yourhandle",
  linkedin:     "https://www.linkedin.com/in/yourname",
  behance:      "https://www.behance.net/yourname",
  pixieset:     "https://yourname.pixieset.com",
  format:       "https://yourname.format.com",
};

function defForId(id: string): IntegrationDef | undefined {
  return INTEGRATIONS.find((i) => i.id === id);
}

// Coerce common shorthand into a usable URL. People paste
// "opentable.com/r/foo" or "www.example.com" all the time. Auto-prepend
// https:// when the input is plausibly a domain. Return null if input
// looks broken (whitespace, no dot, garbage).
function coerceUrl(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (/\s/.test(v)) return null;
  if (/^https?:\/\//i.test(v)) {
    // Already has scheme — basic sanity: must have a dot somewhere after //.
    return /\.[a-z]{2,}/i.test(v) ? v : null;
  }
  // Treat as bare domain. Require something that looks like a TLD.
  if (!/\.[a-z]{2,}/i.test(v)) return null;
  return `https://${v}`;
}

export default function SitePlugins() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [plugins, setPlugins] = useState<Plugin | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);
  // Integrations changed this session that are not on the built site yet.
  const [pendingApply, setPendingApply] = useState<string[]>([]);
  const [rebuilding, setRebuilding] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    api.getPlugins(siteId).then(setPlugins).catch((e) => setErr(String(e)));
  }, [siteId]);

  function flashToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  }

  // Active rows = the integrations on the brief, minus OAuth-only ones
  // (they live in the "Coming soon" section).
  const activeRows = useMemo(() => {
    if (!plugins) return [] as IntegrationDef[];
    return (plugins.integrations ?? [])
      .map(defForId)
      .filter((d): d is IntegrationDef => !!d && !OAUTH_INTEGRATIONS.has(d.id));
  }, [plugins]);

  // Add-able rows = catalog filtered to business type, minus already-on-brief, minus OAuth.
  const addableRows = useMemo(() => {
    if (!plugins) return [] as IntegrationDef[];
    const have = new Set(plugins.integrations ?? []);
    const list = plugins.businessType
      ? integrationsForType(plugins.businessType as BusinessType)
      : [];
    return list.filter((d) => !have.has(d.id) && !OAUTH_INTEGRATIONS.has(d.id));
  }, [plugins]);

  // OAuth rows = OAuth-only integrations relevant to this business
  // type. Shown as disabled "Coming soon" so the customer sees they
  // exist but knows they aren't ready yet.
  const oauthRows = useMemo(() => {
    if (!plugins) return [] as IntegrationDef[];
    const list = plugins.businessType
      ? integrationsForType(plugins.businessType as BusinessType)
      : [];
    return list.filter((d) => OAUTH_INTEGRATIONS.has(d.id));
  }, [plugins]);

  async function connect(integrationId: string) {
    if (!siteId) return;
    const raw = drafts[integrationId] ?? "";
    const url = coerceUrl(raw);
    if (!url) {
      setErr(
        raw.trim()
          ? `Couldn't read "${raw.trim()}" as a URL. Try ${PLACEHOLDER[integrationId] ?? "https://..."}`
          : "Paste a URL first."
      );
      return;
    }
    setBusy(integrationId);
    setErr(null);
    try {
      const updated = await api.updatePlugin(siteId, integrationId, { url });
      setPlugins((p) => p && ({ ...p, integrationConfig: updated.integrationConfig, integrations: updated.integrations }));
      setDrafts((d) => ({ ...d, [integrationId]: "" }));
      setPendingApply((ids) => ids.includes(integrationId) ? ids : [...ids, integrationId]);
      flashToast("Connected and saved. Build your site to put it on the page.");
    } catch (e: any) {
      setErr(e?.payload?.error || String(e));
    } finally {
      setBusy(null);
    }
  }

  async function disconnect(integrationId: string) {
    if (!siteId) return;
    setBusy(integrationId);
    setErr(null);
    try {
      const updated = await api.updatePlugin(siteId, integrationId, {});
      setPlugins((p) => p && ({ ...p, integrationConfig: updated.integrationConfig, integrations: updated.integrations }));
      setPendingApply((ids) => ids.includes(integrationId) ? ids : [...ids, integrationId]);
      flashToast("Disconnected. It comes off the page on your site's next build.");
    } catch (e: any) {
      setErr(e?.payload?.error || String(e));
    } finally {
      setBusy(null);
    }
  }

  async function rebuildNow() {
    if (!siteId || rebuilding) return;
    setRebuilding(true);
    setErr(null);
    try {
      const r = await api.startBuild(siteId, "preview");
      navigate(`/sites/${siteId}/builds/${r.jobId}`);
    } catch (e: any) {
      setErr(e?.payload?.error || String(e));
      setRebuilding(false);
    }
  }

  function startAdding(integrationId: string) {
    setDrafts((d) => ({ ...d, [integrationId]: d[integrationId] ?? "" }));
    setPlugins((p) => p && ({
      ...p,
      integrations: [...(p.integrations ?? []), integrationId],
    }));
  }

  if (err && !plugins) return <div className="card" style={{ borderColor: "#fee2e2", color: "#b91c1c" }}>{err}</div>;
  if (!plugins) return <div className="muted">Loading plugins…</div>;

  return (
    <div className="stack site-workspace">
      {toast && (
        <div
          className="card"
          style={{
            borderColor: "#bbf7d0",
            background: "#ecfdf5",
            color: "#065f46",
            padding: "10px 14px",
            fontSize: 13,
          }}
        >
          ✓ {toast}
        </div>
      )}

      <div className="row between">
        <h1 style={{ margin: 0 }}>Plugins &amp; integrations</h1>
        <Link to={`/sites/${siteId}`} className="btn">← back to site</Link>
      </div>
      <div className="muted" style={{ fontSize: 13 }}>
        Paste the link customers use to find or book you. Connections are
        saved immediately and get placed on your site the next time it is built.
      </div>

      {err && (
        <div className="card" style={{ borderColor: "#fee2e2", color: "#b91c1c" }}>{err}</div>
      )}

      {pendingApply.length > 0 && (
        <div className="card row between" style={{ borderColor: "#bfdbfe", background: "#eff6ff", padding: "12px 14px", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 13, color: "#1e3a8a" }}>
            {pendingApply.length === 1 ? "1 integration change is" : `${pendingApply.length} integration changes are`} saved but not on your site yet.
          </span>
          <button type="button" className="btn primary" onClick={() => void rebuildNow()} disabled={rebuilding}>
            {rebuilding ? "Starting build…" : "Build them into my preview"}
          </button>
        </div>
      )}

      {activeRows.length === 0 && (
        <div className="card muted">
          You haven't connected any integrations yet. Pick one below to add.
        </div>
      )}

      {activeRows.map((def) => {
        const config = plugins.integrationConfig[def.id] ?? {};
        const connected = !!config.url;
        const isBusy = busy === def.id;
        return (
          <div key={def.id} className="card stack" style={{ gap: 10 }}>
            <div className="row between" style={{ alignItems: "flex-start" }}>
              <div className="stack" style={{ gap: 2, minWidth: 0 }}>
                <strong>{def.label}</strong>
                <span className="muted" style={{ fontSize: 12 }}>{def.note}</span>
              </div>
              <span className={`badge ${connected ? "good" : ""}`}>
                {connected ? "Connected" : "Not connected"}
              </span>
            </div>

            {connected ? (
              <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <a
                  href={config.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {config.url}
                </a>
                <button
                  type="button"
                  className="btn"
                  onClick={() => disconnect(def.id)}
                  disabled={isBusy}
                >
                  {isBusy ? "Disconnecting…" : "Disconnect"}
                </button>
              </div>
            ) : (
              <form
                className="stack"
                style={{ gap: 8 }}
                onSubmit={(e) => { e.preventDefault(); connect(def.id); }}
              >
                <input
                  type="text"
                  placeholder={PLACEHOLDER[def.id] ?? "https://…"}
                  value={drafts[def.id] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [def.id]: e.target.value }))}
                  disabled={isBusy}
                  style={{ width: "100%" }}
                />
                <div className="row" style={{ justifyContent: "flex-end" }}>
                  <button type="submit" className="btn primary" disabled={isBusy}>
                    {isBusy ? "Connecting…" : "Connect"}
                  </button>
                </div>
              </form>
            )}
          </div>
        );
      })}

      {addableRows.length > 0 && (
        <div className="card stack" style={{ gap: 10 }}>
          <div className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
            Add another integration
          </div>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {addableRows.map((def) => (
              <button
                key={def.id}
                type="button"
                className="btn"
                onClick={() => startAdding(def.id)}
                style={{ fontSize: 12, padding: "4px 10px" }}
                title={def.note}
              >
                + {def.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {oauthRows.length > 0 && (
        <div className="card stack" style={{ gap: 10, opacity: 0.85 }}>
          <div className="row between" style={{ alignItems: "center" }}>
            <div className="stack" style={{ gap: 2 }}>
              <strong>Coming soon</strong>
              <span className="muted" style={{ fontSize: 12 }}>
                Services that need a sign-in or API key — we'll add proper connect flows for these.
              </span>
            </div>
          </div>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {oauthRows.map((def) => (
              <span
                key={def.id}
                className="badge"
                style={{ fontSize: 12, padding: "4px 10px", color: "#6b7280", background: "#f3f4f6", border: "1px solid var(--border)" }}
                title={`${def.note} — coming soon`}
              >
                {def.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
